import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Instagram } from "lucide-react";
import {
  CLINIC_NAME, PHONE_PRIMARY, PHONE_WHATSAPP, PHONE_PRIMARY_TEL,
  EMAIL, INSTAGRAM, INSTAGRAM_URL, CLINIC_ADDRESS, CLINIC_TIMINGS,
  getWhatsAppUrl,
} from "@/lib/constants";
import { CATEGORIES } from "@/lib/seo-categories";

const quickLinks = [
  { name: "Home", path: "/" },
  { name: "About Dr. Mutha", path: "/about" },
  { name: "Invisalign Kalyan", path: "/invisalign-kalyan" },
  { name: "Our Doctors", path: "/associate-doctors" },
  { name: "Smile Gallery", path: "/smile-gallery" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Blog", path: "/blogs" },
  { name: "Book Appointment", path: "/book-appointment" },
  { name: "Contact Us", path: "/contact" },
];

// Mobile-first landing pages (campaign / ad / SEO entry points).
// Add new entries here as more LPs are built.
const mobileLandingPages = [
  { name: "Invisalign Mobile Landing (v1 — Video Hero)", path: "/lp/invisalign-mobile" },
  { name: "Invisalign Mobile Landing (v2 — Scroll Video)", path: "/lp/invisalign-mobile-v2" },
];

// AEO (Answer-Engine Optimization) + GEO (local search) landing pages.
// Built to win AI Overviews, "People Also Ask" snippets, and Google Local Pack.
const aeoGeoPages = [
  { name: "Dental Implants — Kalyan (AEO/GEO)", path: "/lp/dental-implants-kalyan-aeo" },
];

const Footer = () => (
  <footer className="surface-darker text-[hsl(var(--primary-foreground))]">
    <div className="container py-12 md:py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
        {/* Col 1 - About */}
        <div>
          <h3 className="font-heading text-xl font-light tracking-tight mb-4 text-white">{CLINIC_NAME}</h3>
          <span className="gold-hairline" />
          <p className="text-sm text-white/70 leading-relaxed mb-4 font-body">
            Providing world-class dental care in Kalyan with a focus on patient comfort,
            advanced technology, and lasting results.
          </p>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-gold transition-colors">
            <Instagram className="h-4 w-4" /> {INSTAGRAM}
          </a>
        </div>

        {/* Col 2 - Quick Links */}
        <div>
          <h4 className="text-eyebrow !text-white/90 mb-4">Quick Links</h4>
          <ul className="space-y-2">
            {quickLinks.map((link) => (
              <li key={link.path}>
                <Link to={link.path} className="text-sm text-white/80 hover:text-gold transition-colors font-body">
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3 - Services */}
        <div>
          <h4 className="text-eyebrow !text-white/90 mb-4">Our Services</h4>
          <ul className="space-y-2">
            {CATEGORIES.map((cat) => (
              <li key={cat.key}>
                <Link
                  to={`/services/${cat.hubSlug}`}
                  className="text-sm text-white/80 hover:text-gold transition-colors font-body"
                >
                  {cat.name}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/services" className="text-sm text-gold/90 hover:text-gold transition-colors font-body">
                View all services →
              </Link>
            </li>
          </ul>
        </div>

        {/* Col 4 - Contact */}
        <div>
          <h4 className="text-eyebrow !text-white/90 mb-4">Contact Us</h4>
          <ul className="space-y-3">
            <li className="flex items-start gap-2 text-sm">
              <Phone className="h-4 w-4 mt-0.5 shrink-0 text-gold" />
              <div>
                <a href={PHONE_PRIMARY_TEL} className="text-white/90 hover:text-gold transition-colors block font-body">{PHONE_PRIMARY}</a>
                <a href={getWhatsAppUrl()} className="text-white/90 hover:text-gold transition-colors block font-body">{PHONE_WHATSAPP} (WhatsApp)</a>
              </div>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Mail className="h-4 w-4 mt-0.5 shrink-0 text-gold" />
              <a href={`mailto:${EMAIL}`} className="text-white/90 hover:text-gold transition-colors font-body">{EMAIL}</a>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-gold" />
              <span className="text-white/80 font-body">{CLINIC_ADDRESS}</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Clock className="h-4 w-4 mt-0.5 shrink-0 text-gold" />
              <div className="text-white/80 font-body">
                <p>Mon-Sat: {CLINIC_TIMINGS.weekdays}</p>
                <p>Sunday: {CLINIC_TIMINGS.sunday}</p>
                <p>Closed: {CLINIC_TIMINGS.closed}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* New Mobile Landing Pages — collapsible disclosure */}
      <div className="mt-10 pt-8 border-t border-white/10">
        <details className="group">
          <summary className="cursor-pointer list-none flex items-center gap-2 text-eyebrow !text-white/90 hover:!text-gold transition-colors">
            New Mobile Landing Pages
            <span className="transition-transform group-open:rotate-180 text-gold">▾</span>
          </summary>
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            {mobileLandingPages.map((lp) => (
              <li key={lp.path}>
                <Link
                  to={lp.path}
                  className="text-sm text-white/80 hover:text-gold transition-colors font-body"
                >
                  {lp.name} →
                </Link>
              </li>
            ))}
          </ul>
        </details>

        <details className="group mt-6">
          <summary className="cursor-pointer list-none flex items-center gap-2 text-eyebrow !text-white/90 hover:!text-gold transition-colors">
            AEO/GEO Pages
            <span className="transition-transform group-open:rotate-180 text-gold">▾</span>
          </summary>
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            {aeoGeoPages.map((lp) => (
              <li key={lp.path}>
                <Link
                  to={lp.path}
                  className="text-sm text-white/80 hover:text-gold transition-colors font-body"
                >
                  {lp.name} →
                </Link>
              </li>
            ))}
          </ul>
        </details>
      </div>
    </div>

    {/* Bottom Bar — gold hairline */}
    <div className="border-t border-gold/30">
      <div className="container py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/80 font-body">
        <p>© {new Date().getFullYear()} {CLINIC_NAME}. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <Link to="/privacy-policy" className="hover:text-gold transition-colors">Privacy Policy</Link>
          <Link to="/terms-conditions" className="hover:text-gold transition-colors">Terms & Conditions</Link>
          <Link to="/admin/login" className="hover:text-gold transition-colors text-white/70">Admin</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
